import React, { useMemo } from "react";
import { Box } from "@mui/material";
import ActivityBarChart from "../../ActivityBarChart";
import { MetricBucket, metricNumber } from "../useMetrics";
import { TOUCH_QUERY } from "../../../../utils/useViewport";

/** One bar: a core metric id and the label the catalog gives it. */
export interface CoreMetric {
  id: string;
  label: string;
}

interface CoreMetricChartProps {
  metrics: CoreMetric[];
  bucket: MetricBucket | null | undefined;
  /** Slanted 12 px axis labels (desktop); off = the upright narrow style. */
  wideLabels: boolean;
  /** When given, the bars are clickable and hand back their metric. */
  onMetricClick?: (metric: CoreMetric) => void;
}

// The upright labels are longer than the chart's fixed 80 px bottom margin
// ("Finalized B2B Referrals"), so they are let out of the SVG into padding
// kept for them. Touch screens get them at 12 px, as on the dashboard card.
const narrowSx = {
  pb: "40px",
  "& .recharts-surface": { overflow: "visible" },
  [`@media ${TOUCH_QUERY}`]: {
    pb: "64px",
    "& .recharts-xAxis .recharts-cartesian-axis-tick-value": {
      fontSize: "12px",
    },
  },
};

/** The nine core metrics of one bucket as the old Program Statistics bars. */
const CoreMetricChart: React.FC<CoreMetricChartProps> = ({
  metrics,
  bucket,
  wideLabels,
  onMetricClick,
}) => {
  // ActivityBarChart consumes [{ key, value }]; `id` rides along for the click.
  const data = useMemo(
    () =>
      metrics.map((metric) => ({
        key: metric.label,
        value: metricNumber(bucket, metric.id),
        id: metric.id,
      })),
    [metrics, bucket],
  );

  return (
    <Box sx={wideLabels ? undefined : narrowSx}>
      <Box sx={{ height: 300 }}>
        <ActivityBarChart
          data={data}
          wideLabels={wideLabels}
          barClickable={Boolean(onMetricClick)}
          onBarClick={(entry: { id: string; key: string }) =>
            onMetricClick && onMetricClick({ id: entry.id, label: entry.key })
          }
        />
      </Box>
    </Box>
  );
};

export default CoreMetricChart;
